/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Alert } from 'sarsaparilla';

import {
    AdditionalChallengeRequiredAlert,
    RecaptchaFailedAlert,
} from '../../../recaptcha';

export type LoginFormErrorType =
    | 'unknown'
    | 'badCredentials'
    | 'lockout'
    | 'additionalChallenge'
    | 'recaptchaFailed'
    | null;

interface LoginFormErrorsProps {
    errorType: LoginFormErrorType;
    className?: string;
}

export function LoginFormErrors({ errorType, className }: LoginFormErrorsProps) {
    if (!errorType) {
        return null;
    }

    if (errorType === 'additionalChallenge') {
        return <AdditionalChallengeRequiredAlert className={className} />;
    }

    if (errorType === 'recaptchaFailed') {
        return <RecaptchaFailedAlert className={className} />;
    }

    if (errorType === 'badCredentials') {
        return (
            <Alert
                type="error"
                className={className}
                heading="Could Not Log You In"
                shouldFocusOnMount
            >
                Are you sure you have the right email and password? Please double check
                and try again.
            </Alert>
        );
    }

    if (errorType === 'lockout') {
        return (
            <Alert
                type="error"
                className={className}
                heading="Account Locked"
                shouldFocusOnMount
            >
                This account has been locked out for 30 minutes due to three or more
                failed login attempts. To access your account before then, check your
                e-mail for a magic login link or click{' '}
                <strong className="font-weight-semibold">Forgot Your Password?</strong>{' '}
                below.
            </Alert>
        );
    }

    return (
        <Alert
            type="error"
            className={className}
            heading="Login Failed"
            shouldFocusOnMount
        >
            Something went wrong while trying to log you in. Please try again in a moment.
            If the problem persists, try refreshing the page.
        </Alert>
    );
}

// cSpell:ignore couldn
